/**
 * Centered confirmation dialog (matches the mockup's confirmDialog).
 *
 * Three tones:
 *   "create"  — blue/green, ✓ icon
 *   "warn"    — amber, ⏻ icon
 *   "danger"  — red, ⚠ icon
 *   "success" — green, ✓ icon, no Cancel
 */
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./ConfirmDialog.module.scss";

export type ConfirmTone = "create" | "warn" | "danger" | "success";

interface ConfirmDialogProps {
  title: string;
  /** Supports HTML-ish text via dangerouslySetInnerHTML. */
  text: string;
  tone?: ConfirmTone;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Called when the confirm button is clicked. Absent for "success" tone. */
  onConfirm?: () => void;
  onCancel: () => void;
  /** Optional secondary action (e.g. "Add Group Admin" on success). */
  secondaryLabel?: string;
  secondaryIcon?: string;
  onSecondary?: () => void;
  /** Override the tone's default glyph. */
  icon?: string;
  /** Extra content between the text and the buttons — e.g. a change summary. */
  children?: React.ReactNode;
}

const TONE_ICON: Record<ConfirmTone, string> = {
  create: "fa-layer-group",
  warn: "fa-power-off",
  danger: "fa-triangle-exclamation",
  success: "fa-check",
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title, text, tone = "danger",
  confirmLabel = "Confirm", cancelLabel = "Cancel",
  onConfirm, onCancel,
  secondaryLabel, secondaryIcon, onSecondary, icon, children,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onCancel();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const btnClass =
    tone === "warn" ? styles.btnWarn
    : tone === "create" ? styles.btnCreate
    : tone === "success" ? styles.btnSuccess
    : styles.btnDanger;

  return createPortal(
    <div className={styles.overlay} ref={rootRef} onMouseDown={onCancel}>
      <div className={styles.card} onMouseDown={(e) => e.stopPropagation()}>
        <button className={styles.close} onClick={onCancel} aria-label="Close">
          <i className="fas fa-xmark" />
        </button>

        <div className={`${styles.icon} ${styles[tone]}`}>
          <i className={`fas ${icon ?? TONE_ICON[tone]}`} />
        </div>
        <h3 className={styles.title}>{title}</h3>
        <p className={styles.text} dangerouslySetInnerHTML={{ __html: text }} />
        {children}

        <div className={styles.buttons}>
          {tone !== "success" && (
            <button className={styles.btnGhost} onClick={onCancel}>
              {cancelLabel}
            </button>
          )}
          {tone === "success" ? (
            <>
              <button className={styles.btnGhost} onClick={onCancel}>
                {cancelLabel || "Later"}
              </button>
              {onSecondary && (
                <button className={btnClass} onClick={onSecondary}>
                  {secondaryIcon && <i className={`fas ${secondaryIcon}`} />}
                  {secondaryLabel}
                </button>
              )}
            </>
          ) : onConfirm && (
            <button className={btnClass} onClick={onConfirm}>
              <i className={`fas ${icon ?? TONE_ICON[tone]}`} /> {confirmLabel}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};
